import { setCurrentAnnotationId } from "@/store/ai-asset-review/ai-asset-review-slice";
import { selectAiReviewStats } from "@/store/ai-asset-review/ai-asset-review-selectors";
import { changeMode } from "@/store/mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import {
  FaroButton,
  FaroChip,
  FaroText,
  FontWeights,
  green,
  blue,
  grey,
  red,
  NO_TRANSLATE_CLASS,
  TranslateVar,
} from "@faro-lotv/flat-ui";
import CheckIcon from "@mui/icons-material/Check";
import { Box, Divider, Stack } from "@mui/material";
import { useCallback, useMemo, type JSX } from "react";

/** @returns Finish screen shown when all AI annotations have been reviewed */
export function AiReviewFinishScreen(): JSX.Element {
  const dispatch = useAppDispatch();
  const stats = useAppSelector(selectAiReviewStats);

  const approvalRate = useMemo(() => {
    if (stats.total === 0) return 0;
    return Math.round((stats.approved / stats.total) * 100);
  }, [stats.approved, stats.total]);

  const handleReturnToMain = useCallback(() => {
    dispatch(setCurrentAnnotationId(undefined));
    dispatch(changeMode("sheet"));
  }, [dispatch]);

  return (
    <Stack
      sx={{
        width: "100%",
        minHeight: "100%",
        alignItems: "center",
        px: 3,
        py: 2,
      }}
    >
      <Stack
        sx={{
          maxWidth: "500px",
          width: "100%",
          alignItems: "center",
          gap: 3,
          my: "auto",
        }}
      >
        {/* Success Icon */}
        <Box
          component="div"
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            backgroundColor: `${green[50]}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: `2px solid ${green[100]}`,
          }}
        >
          <CheckIcon
            sx={{
              fontSize: 32,
              color: green[700],
            }}
          />
        </Box>

        {/* Title */}
        <FaroText
          variant="heading24"
          fontWeight={FontWeights.SemiBold}
          color={grey[900]}
        >
          Review complete!
        </FaroText>

        {/* Description */}
        <FaroText
          variant="bodyM"
          color={grey[700]}
          sx={{ textAlign: "center" }}
        >
          All <TranslateVar name="count">{stats.total}</TranslateVar> AI
          generated annotation(s) have been reviewed. Your review session has
          ended and no further changes can be made.
        </FaroText>

        {/* Review Summary Card */}
        <Stack
          component="div"
          sx={{
            gap: 3,
            width: "100%",
            border: `1px solid ${grey[200]}`,
            borderRadius: 1,
            p: 3,
          }}
        >
          <FaroText
            variant="heading14"
            fontWeight={FontWeights.SemiBold}
            color={grey[900]}
          >
            Review summary
          </FaroText>

          <Stack divider={<Divider sx={{ my: 1, mb: 2 }} />}>
            {/* Total */}
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                px: 1,
              }}
            >
              <FaroText variant="bodyM" color={grey[700]}>
                Total
              </FaroText>
              <FaroText
                variant="bodyM"
                color={grey[900]}
                className={NO_TRANSLATE_CLASS}
              >
                {stats.total}
              </FaroText>
            </Stack>

            {/* Approved */}
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                px: 1,
              }}
            >
              <FaroText variant="bodyM" color={grey[700]}>
                Approved
              </FaroText>
              <FaroChip
                label={stats.approved.toString()}
                backgroundColor={green[50]}
                color={green[700]}
                size="small"
                variant="outlined"
                borderColor={green[100]}
                className={NO_TRANSLATE_CLASS}
              />
            </Stack>

            {/* Rejected */}
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                px: 1,
              }}
            >
              <FaroText variant="bodyM" color={grey[700]}>
                Rejected
              </FaroText>
              <FaroChip
                label={stats.rejected.toString()}
                backgroundColor={red[50]}
                color={red[500]}
                size="small"
                variant="outlined"
                borderColor={red[100]}
                className={NO_TRANSLATE_CLASS}
              />
            </Stack>

            {/* Approval Rate */}
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                px: 1,
              }}
            >
              <FaroText
                variant="bodyM"
                color={grey[700]}
                fontWeight={FontWeights.SemiBold}
              >
                Approval rate
              </FaroText>
              <FaroText
                variant="bodyM"
                fontWeight={FontWeights.SemiBold}
                color={blue[500]}
                className={NO_TRANSLATE_CLASS}
              >
                {approvalRate}%
              </FaroText>
            </Stack>
          </Stack>
        </Stack>

        {/* Return Button */}
        <FaroButton
          variant="primary"
          onClick={handleReturnToMain}
          size="l"
          sx={{ width: "100%" }}
        >
          Return to main interface
        </FaroButton>

        {/* Note */}
        <FaroText
          variant="bodyS"
          color={grey[600]}
          sx={{ textAlign: "center" }}
        >
          Note: you cannot undo any action at this point
        </FaroText>
      </Stack>
    </Stack>
  );
}
